import {
  Box,
  Button,
  getContrastRatio,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { cyan, green, lime, orange, pink } from '@mui/material/colors'
import { useNavigate } from 'react-router-dom'

import useGetAuthSessionQuery from '~/features/auth/hooks/useGetAuthSessionQuery'
import useGetUser from '~/features/user/hooks/useGetUser'
import { EAdminPortalClientPath, EClientPath } from '~/models/api'
import { ECognitoGroups } from '~/models/auth'
import { store } from '~/store'
import pathBuilder from '~/utils/api'
import rem from '~/utils/ui'

function ImpersonationBanner() {
  const [
    impersonatorsOrgId,
    impersonatorsUserId,
    setUser,
    impersonatedUserId,
    impersonatedUserOrgId,
  ] = store((state) => [
    state.user.impersonatorsOrgId,
    state.user.impersonatorsUserId,
    state.setUser,
    state.user.id,
    state.user.organizationId,
  ])

  const navigate = useNavigate()

  const { data: user } = useGetUser(impersonatedUserId)
  const { data: authSession } = useGetAuthSessionQuery()

  const userEmail = user?.data?.email || ''

  const isImpersonating = !!impersonatorsUserId
  const theme = useTheme()

  if (!isImpersonating) {
    return null
  }

  const isAdmin = authSession?.groups.includes(ECognitoGroups.admin)

  const neonColors = [green.A400, pink.A400, cyan.A400, orange.A400, lime.A400]
  const lightText = theme.palette.common.white
  const darkText = theme.palette.common.black
  const { mainBackground } = theme.palette

  const backgroundColor = neonColors.reduce((bestColor, currentColor) =>
    getContrastRatio(currentColor, mainBackground) >
    getContrastRatio(bestColor, mainBackground)
      ? currentColor
      : bestColor,
  )

  const textColor =
    getContrastRatio(lightText, backgroundColor) >
    getContrastRatio(darkText, backgroundColor) * 0.4
      ? lightText
      : darkText

  const handleLogoutFromViewClick = () => {
    setUser({
      impersonatorsUserId: '',
      impersonatorsOrgId: '',
      organizationId: impersonatorsOrgId,
      id: impersonatorsUserId,
    })

    if (isAdmin) {
      navigate(
        pathBuilder(EAdminPortalClientPath.user, { id: impersonatedUserOrgId }),
      )
    } else {
      navigate(
        pathBuilder(EClientPath.partnerAdminUserManagementById, {
          userId: impersonatedUserId,
        }),
      )
    }
  }

  return (
    <Box
      sx={{
        backgroundColor,
        padding: '5px 0',
        textAlign: 'center',
      }}
    >
      <Stack
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          paddingInline: { xxxs: rem(20), sm: { xxxs: rem(10), sm: rem(50) } },
          paddingBlock: rem(5),
        }}
      >
        <Typography
          sx={{
            color: textColor,
            mr: rem(10),
            fontSize: { xxxs: rem(11), sm: rem(15) },
          }}
        >
          Currently viewing as {userEmail}
        </Typography>

        <Button
          variant="secondary"
          onClick={handleLogoutFromViewClick}
          sx={{
            color: textColor,
            fontSize: { xxs: rem(9), sm: rem(13) },
            borderColor: textColor,
            height: 'auto',
            width: 'auto',
            minWidth: rem(90),
            paddingInline: { xxxs: rem(15) },
            cursor: 'pointer',
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            textDecoration: 'none',
            '&:hover': { color: textColor, borderColor: textColor },
          }}
        >
          Logout from current user
        </Button>
      </Stack>
    </Box>
  )
}

export default ImpersonationBanner
